import type { MouseEvent, PointerEvent } from "react";
import { AlertTriangleIcon, ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { formatAgentCountLabel } from "./sidebarWorkerRows.logic";

interface SidebarThreadAgentSummaryProps {
  readonly workerCount: number;
  readonly attentionCount: number;
}

export function SidebarThreadAgentSummary({
  workerCount,
  attentionCount,
}: SidebarThreadAgentSummaryProps) {
  if (workerCount === 0) return null;
  return (
    <>
      <span className="whitespace-nowrap text-[10px] text-muted-foreground/50">
        {formatAgentCountLabel(workerCount)}
      </span>
      {attentionCount > 0 ? (
        <span
          className="inline-flex items-center gap-0.5 text-[10px] text-warning-foreground"
          aria-label={`${formatAgentCountLabel(attentionCount)} ${attentionCount === 1 ? "needs" : "need"} attention`}
        >
          <AlertTriangleIcon className="size-3" />
          {attentionCount}
        </span>
      ) : null}
    </>
  );
}

interface SidebarThreadWorkerToggleProps {
  readonly threadKey: string;
  readonly threadId: string;
  readonly workerCount: number;
  readonly workersExpanded: boolean;
  readonly reserveArchiveSpace: boolean;
  readonly onToggleWorkers: ((threadKey: string) => void) | undefined;
  readonly onPointerDown: (event: PointerEvent<HTMLButtonElement>) => void;
}

export function SidebarThreadWorkerToggle({
  threadKey,
  threadId,
  workerCount,
  workersExpanded,
  reserveArchiveSpace,
  onToggleWorkers,
  onPointerDown,
}: SidebarThreadWorkerToggleProps) {
  if (workerCount === 0) return null;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    onToggleWorkers?.(threadKey);
  };

  return (
    <button
      type="button"
      data-thread-selection-safe
      data-testid={`worker-chevron-${threadId}`}
      aria-label={workersExpanded ? "Collapse agents" : "Expand agents"}
      className={`${reserveArchiveSpace ? "mr-5 " : ""}ml-0.5 inline-flex size-4 shrink-0 cursor-pointer items-center justify-center text-muted-foreground/40 hover:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring`}
      onPointerDown={onPointerDown}
      onClick={handleClick}
    >
      {workersExpanded ? (
        <ChevronDownIcon className="size-3" />
      ) : (
        <ChevronRightIcon className="size-3" />
      )}
    </button>
  );
}
